import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import InteractiveMap from "@/components/InteractiveMap";
import PreOrderModal from "@/components/PreOrderModal";
import { products as pApi, dispensaries as dApi, mlApi } from "@/lib/api";
import { useStateCtx } from "@/context/StateContext";
import { useAuth } from "@/context/AuthContext";
import { Slider } from "@/components/ui/slider";
import { Filter, X, Search, Store, ArrowUpDown, Sparkles, Flame } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const CATEGORIES = ["all", "flower", "vape", "edible", "concentrate", "tincture", "preroll", "holistic", "mushroom"];
const VAPE_SUBTYPES = ["all", "cart", "pod", "disposable", "battery"];
const MUSHROOM_SUBTYPES = ["all", "amanita", "functional", "microdose"];
const STRAINS = ["all", "indica", "sativa", "hybrid", "cbd"];
const EFFECTS = ["all", "relax", "energy", "focus", "sleep", "creative", "euphoric"];
const SORTS = [
  { v: "value", l: "Best Value" },
  { v: "price", l: "Cheapest" },
  { v: "rating", l: "Top Rated" },
  { v: "thc", l: "Loudest THC" },
];

export default function Discover() {
  const { stateCode } = useStateCtx();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [filters, setFilters] = useState({
    category: params.get("category") || "all", subtype: "all",
    strain: "all", effect: "all",
    minPrice: 0, maxPrice: 100, sort: "value", q: "",
  });
  const [items, setItems] = useState([]);
  const [disps, setDisps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDisp, setActiveDisp] = useState(null);
  const [preorderShop, setPreorderShop] = useState(null);
  const [recs, setRecs] = useState({ items: [], cold_start: true });
  const [trending, setTrending] = useState({ items: [], fallback: true });

  useEffect(() => {
    const stateParam = stateCode === "ALL" ? undefined : stateCode;
    dApi.list({ state: stateParam }).then(setDisps);
  }, [stateCode]);

  useEffect(() => {
    setLoading(true);
    const stateParam = stateCode === "ALL" ? undefined : stateCode;
    pApi.list({
      state: stateParam, dispensary_id: activeDisp,
      category: filters.category === "all" ? undefined : filters.category,
      subtype: filters.subtype === "all" ? undefined : filters.subtype,
      strain: filters.strain === "all" ? undefined : filters.strain,
      effect: filters.effect === "all" ? undefined : filters.effect,
      min_price: filters.minPrice || undefined,
      max_price: filters.maxPrice < 100 ? filters.maxPrice : undefined,
      sort: filters.sort, limit: 60,
    }).then(setItems).finally(() => setLoading(false));
  }, [filters, stateCode, activeDisp]);

  // Personalized + Trending rails
  useEffect(() => {
    const stateParam = stateCode === "ALL" ? undefined : stateCode;
    mlApi.trending(stateParam, 6).then(setTrending).catch(() => {});
    if (user) mlApi.recommendations(stateParam, 6).then(setRecs).catch(() => {});
  }, [stateCode, user]);

  const filtered = useMemo(() => {
    if (!filters.q) return items;
    const q = filters.q.toLowerCase();
    return items.filter((p) => p.name.toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q) || (p.flavors || []).join(" ").toLowerCase().includes(q));
  }, [items, filters.q]);

  // Reset subtype when category changes
  useEffect(() => {
    setFilters((f) => ({ ...f, subtype: "all" }));
    // eslint-disable-next-line
  }, [filters.category]);

  const subtypeOptions = filters.category === "vape" ? VAPE_SUBTYPES : filters.category === "mushroom" ? MUSHROOM_SUBTYPES : null;

  const onCardClick = (p) => {
    // Fire view signal
    if (user) mlApi.signal("view", "product", p.id);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 380, height: 380, background: "#15FF73", top: -100, right: -80 }} />
      <NavBar />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-10 relative">
        <div className="flex items-center gap-2 mb-3">
          <span className="sticker sticker-green"><span className="pulse-dot mr-1" /> Live · {stateCode}</span>
          <span className="sticker sticker-magenta">Sorted: {SORTS.find((s) => s.v === filters.sort)?.l}</span>
        </div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.92] mb-3">
          The <span className="text-green-gradient italic">catalog</span>, ranked.
        </h1>
        <p className="text-lg text-[#0B0F0C]/65 max-w-2xl mb-8">Every product scored. Mid filtered out. Find the gas, fast.</p>

        {/* Personalized + Trending rails */}
        {user && recs.items?.length > 0 && !recs.cold_start && (
          <Rail
            title="For you"
            subtitle="Bud's learning your taste — these match your vibe"
            icon={<Sparkles size={14} strokeWidth={2.4} />}
            tone="card-magenta"
            items={recs.items}
            onCardClick={onCardClick}
            badgeKey="match_reason"
            testid="discover-foryou"
          />
        )}
        {trending.items?.length > 0 && !trending.fallback && (
          <Rail
            title="Trending now"
            subtitle="Most-clicked + claimed in the last 24h"
            icon={<Flame size={14} strokeWidth={2.4} />}
            tone="card-sunset"
            items={trending.items}
            onCardClick={onCardClick}
            badgeKey="momentum"
            badgePrefix="🔥"
            testid="discover-trending"
          />
        )}

        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <label className="flex-1 flex items-center gap-3 rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] focus-within:bg-white focus-within:shadow-[3px_3px_0_#0B0F0C] px-5 h-12 transition-all">
            <Search size={14} strokeWidth={2.4} />
            <input
              data-testid="discover-search"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search strains, brands, flavors..."
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#0B0F0C]/45 font-medium"
            />
            {filters.q && <button onClick={() => setFilters({ ...filters, q: "" })}><X size={14} /></button>}
          </label>
          <div className="flex items-center gap-3">
            <ArrowUpDown size={14} className="opacity-60" />
            <Select value={filters.sort} onValueChange={(v) => setFilters({ ...filters, sort: v })}>
              <SelectTrigger data-testid="sort-select" className="w-[200px] h-12 rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] font-bold">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8]">
                {SORTS.map((s) => <SelectItem key={s.v} value={s.v} className="rounded-lg">{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 pb-20">
          <aside className="lg:col-span-3">
            <div className="card-x card-green p-5 bg-[#FAF6E8] sticky top-24">
              <div className="label-overline text-[#FF1A8C] mb-4 flex items-center gap-2"><Filter size={12} strokeWidth={2.4} /> Filters</div>

              <Group label="Category"><Pills options={CATEGORIES} active={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} testidPrefix="cat" /></Group>
              {subtypeOptions && (
                <Group label={filters.category === "vape" ? "Vape type" : "Mushroom type"}>
                  <Pills options={subtypeOptions} active={filters.subtype} onChange={(v) => setFilters({ ...filters, subtype: v })} testidPrefix="subtype" />
                </Group>
              )}
              <Group label="Strain"><Pills options={STRAINS} active={filters.strain} onChange={(v) => setFilters({ ...filters, strain: v })} testidPrefix="strain" /></Group>
              <Group label="Effect"><Pills options={EFFECTS} active={filters.effect} onChange={(v) => setFilters({ ...filters, effect: v })} testidPrefix="effect" /></Group>

              <Group label={`Price · $${filters.minPrice}-$${filters.maxPrice}`}>
                <Slider data-testid="price-slider" value={[filters.minPrice, filters.maxPrice]} min={0} max={100} step={5}
                  onValueChange={([min, max]) => setFilters({ ...filters, minPrice: min, maxPrice: max })} className="mt-3" />
              </Group>

              <div className="my-5 h-0.5 bg-[#0B0F0C]/15 rounded-full" />
              <div className="label-overline text-[#FF1A8C] mb-3 flex items-center gap-2"><Store size={12} strokeWidth={2.4} /> Dispensaries</div>
              <div className="space-y-1.5 max-h-[260px] overflow-auto pr-2">
                <button onClick={() => setActiveDisp(null)} className={`w-full text-left px-3 py-2 text-xs rounded-xl transition-colors font-bold ${!activeDisp ? "bg-[#15FF73]" : "hover:bg-[#0B0F0C]/5"}`}>
                  All in {stateCode}
                </button>
                {disps.map((d) => (
                  <button key={d.id} data-testid={`disp-filter-${d.id}`} onClick={() => setActiveDisp(d.id)}
                    className={`w-full text-left px-3 py-2 text-xs rounded-xl transition-colors font-bold ${activeDisp === d.id ? "bg-[#15FF73]" : "hover:bg-[#0B0F0C]/5"}`}>
                    <div className="truncate">{d.name}</div>
                    <div className="text-[10px] text-[#0B0F0C]/50 font-mono-x mt-0.5">{d.city}, {d.state} · {d.type}</div>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <main className="lg:col-span-9">
            {/* INTERACTIVE MAP */}
            <div className="mb-6">
              <InteractiveMap
                shops={disps}
                stateCode={stateCode}
                activeId={activeDisp}
                onPick={(s) => setActiveDisp(s.id)}
                onPreorder={(s) => setPreorderShop(s)}
                height={420}
              />
            </div>

            <div className="text-sm text-[#0B0F0C]/65 mb-5"><span className="font-mono-x font-bold text-[#FF1A8C]">{filtered.length}</span> products</div>

            {loading ? (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {[...Array(6)].map((_, i) => <div key={i} className="card-x bg-[#FAF6E8] aspect-[3/4] animate-pulse" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="card-x p-12 text-center bg-[#FAF6E8]">
                <div className="text-5xl mb-3">🌫️</div>
                <div className="font-display text-3xl font-extrabold mb-2">Nothin'.</div>
                <div className="text-sm text-[#0B0F0C]/60">Widen filters or switch state 👆</div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map((p) => <div key={p.id} onClick={() => onCardClick(p)}><ProductCard p={p} /></div>)}
              </div>
            )}
          </main>
        </div>
      </div>
      <Footer />
      {preorderShop && <PreOrderModal shop={preorderShop} onClose={() => setPreorderShop(null)} />}
    </div>
  );
}

function Rail({ title, subtitle, icon, tone, items, onCardClick, badgeKey, badgePrefix, testid }) {
  return (
    <div className={`card-x ${tone} bg-[#FAF6E8] p-5 mb-6`} data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-overline text-[#FF1A8C] mb-1 flex items-center gap-2">{icon} {title}</div>
          <div className="text-xs text-[#0B0F0C]/60">{subtitle}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {items.map((p) => (
          <a
            key={p.id}
            href={`/product/${p.id}`}
            onClick={() => onCardClick && onCardClick(p)}
            data-testid={`${testid}-card-${p.id}`}
            className="rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8] hover:bg-white hover:shadow-[3px_3px_0_#0B0F0C] transition-all p-3 block"
          >
            <div className="aspect-square rounded-xl border-2 border-[#0B0F0C] overflow-hidden mb-2 bg-[#0B0F0C]/5">
              {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : null}
            </div>
            <div className="font-display font-bold text-sm leading-tight line-clamp-2 mb-1">{p.name}</div>
            <div className="flex items-center justify-between text-[10px] font-mono-x">
              <span className="font-bold">${p.price}</span>
              <span className="text-[#FF1A8C] font-bold">{badgePrefix || ""}{badgeKey === "match_reason" ? p[badgeKey] : (p[badgeKey] !== undefined ? p[badgeKey] : "")}</span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function Group({ label, children }) {
  return (
    <div className="mb-5">
      <div className="label-overline text-[#0B0F0C]/60 mb-2">{label}</div>
      {children}
    </div>
  );
}
function Pills({ options, active, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button key={o} data-testid={`${testidPrefix}-${o}`} onClick={() => onChange(o)}
          className={`text-[10px] font-mono-x font-bold uppercase tracking-widest px-2.5 py-1.5 rounded-full border-2 transition-all ${
            active === o ? "bg-[#15FF73] border-[#0B0F0C] shadow-[2px_2px_0_#0B0F0C]" : "border-[#0B0F0C]/30 hover:border-[#0B0F0C] hover:bg-[#FFE74C]"
          }`}>{o}</button>
      ))}
    </div>
  );
}
